<template>
  <div class="menu-wrapper size-full" ref="menuWrapper">
    <!-- Trigger Slot -->
    <div @click="toggleMenu" @keydown="handleTriggerKeydown">
      <slot name="trigger" />
    </div>

    <!-- Menu Dropdown: teleported to body when appendTo="body" so it overlays page (e.g. inside collapsed sidebar) -->
    <Teleport v-if="appendTo === 'body'" to="body">
      <Transition enter-active-class="menu-enter-active" enter-from-class="menu-enter-from" enter-to-class="menu-enter-to"
        leave-active-class="menu-leave-active" leave-from-class="menu-leave-from" leave-to-class="menu-leave-to">
        <div v-if="isOpen" ref="menuDropdown" class="menu-dropdown menu-dropdown--teleport" :class="`menu-dropdown--${position}`"
          role="menu" :aria-labelledby="menuId" @keydown="handleMenuKeydown">
          <div v-for="(item, index) in items" :key="item.id || index" :class="[
            'menu-item',
            {
              'menu-item--disabled': item.disabled,
              'menu-item--danger': item.variant === 'danger',
              'menu-item--separator': item.type === 'separator'
            }
          ]" :role="item.type === 'separator' ? 'separator' : 'menuitem'"
            :tabindex="item.disabled || item.type === 'separator' ? -1 : 0" @click="handleItemClick(item)"
            @keydown="handleItemKeydown($event, item)">
            <div v-if="item.type === 'separator'" class="menu-separator" />
            <template v-else>
              <a v-if="item.href" :href="item.href" class="menu-item-content menu-item-link">
                <div v-if="item.icon" class="menu-item-icon">
                  <BaseIcon size="small" variant="ghost"><component :is="item.icon" /></BaseIcon>
                </div>
                <span class="menu-item-label">{{ item.label }}</span>
                <div v-if="item.shortcut || item.badge" class="menu-item-right">
                  <span v-if="item.shortcut" class="menu-item-shortcut">{{ item.shortcut }}</span>
                  <span v-if="item.badge" class="menu-item-badge">{{ item.badge }}</span>
                </div>
              </a>
              <div v-else class="menu-item-content">
                <div v-if="item.icon" class="menu-item-icon">
                  <BaseIcon size="small" variant="ghost"><component :is="item.icon" /></BaseIcon>
                </div>
                <span class="menu-item-label">{{ item.label }}</span>
                <div v-if="item.shortcut || item.badge" class="menu-item-right">
                  <span v-if="item.shortcut" class="menu-item-shortcut">{{ item.shortcut }}</span>
                  <span v-if="item.badge" class="menu-item-badge">{{ item.badge }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </Transition>
    </Teleport>
    <Transition v-else enter-active-class="menu-enter-active" enter-from-class="menu-enter-from" enter-to-class="menu-enter-to"
      leave-active-class="menu-leave-active" leave-from-class="menu-leave-from" leave-to-class="menu-leave-to">
      <div v-if="isOpen" ref="menuDropdown" class="menu-dropdown" :class="[
        `menu-dropdown--${position}`,
        { 'menu-dropdown--full-width': fullWidth }
      ]" role="menu" :aria-labelledby="menuId" @keydown="handleMenuKeydown">
        <div v-for="(item, index) in items" :key="item.id || index" :class="[
          'menu-item',
          {
            'menu-item--disabled': item.disabled,
            'menu-item--danger': item.variant === 'danger',
            'menu-item--separator': item.type === 'separator'
          }
        ]" :role="item.type === 'separator' ? 'separator' : 'menuitem'"
          :tabindex="item.disabled || item.type === 'separator' ? -1 : 0" @click="handleItemClick(item)"
          @keydown="handleItemKeydown($event, item)">
          <!-- Separator -->
          <div v-if="item.type === 'separator'" class="menu-separator" />

          <!-- Menu Item Content -->
          <template v-else>
            <a v-if="item.href" :href="item.href" class="menu-item-content menu-item-link">
              <!-- Icon -->
              <div v-if="item.icon" class="menu-item-icon">
                <BaseIcon size="small" variant="ghost">
                  <component :is="item.icon" />
                </BaseIcon>
              </div>

              <!-- Label -->
              <span class="menu-item-label">{{ item.label }}</span>

              <!-- Right side content (shortcut, badge, etc.) -->
              <div v-if="item.shortcut || item.badge" class="menu-item-right">
                <span v-if="item.shortcut" class="menu-item-shortcut">{{ item.shortcut }}</span>
                <span v-if="item.badge" class="menu-item-badge">{{ item.badge }}</span>
              </div>
            </a>
            <div v-else class="menu-item-content">
              <!-- Icon -->
              <div v-if="item.icon" class="menu-item-icon">
                <BaseIcon size="small" variant="ghost">
                  <component :is="item.icon" />
                </BaseIcon>
              </div>

              <!-- Label -->
              <span class="menu-item-label">{{ item.label }}</span>

              <!-- Right side content (shortcut, badge, etc.) -->
              <div v-if="item.shortcut || item.badge" class="menu-item-right">
                <span v-if="item.shortcut" class="menu-item-shortcut">{{ item.shortcut }}</span>
                <span v-if="item.badge" class="menu-item-badge">{{ item.badge }}</span>
              </div>
            </div>
          </template>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, nextTick } from 'vue'
import BaseIcon from '@/components/ui/BaseIcon.vue'
import { useExclusiveOverlay } from '@/composables/useExclusiveOverlay'

interface MenuItem {
  id?: string
  label?: string
  icon?: any
  disabled?: boolean
  variant?: 'default' | 'danger'
  type?: 'item' | 'separator' | string
  shortcut?: string
  badge?: string
  action?: string
  href?: string
  handler?: () => void
}

interface Props {
  modelValue?: boolean
  items: MenuItem[]
  position?: 'top' | 'bottom'
  fullWidth?: boolean
  disabled?: boolean
  /** When 'body', dropdown is teleported to body and positioned fixed so it overlays page (e.g. inside narrow/collapsed sidebar) */
  appendTo?: 'body'
  'aria-label'?: string
}

const props = withDefaults(defineProps<Props>(), {
  position: 'bottom',
  fullWidth: false,
  disabled: false,
  appendTo: undefined
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'item-click': [item: MenuItem]
  'menu-open': []
  'menu-close': []
}>()

// Generate unique ID for accessibility
const menuId = computed(() => `menu-${Math.random().toString(36).substr(2, 9)}`)

// State
const isOpen = computed({
  get: () => props.modelValue || false,
  set: (value) => emit('update:modelValue', value)
})

const menuWrapper = ref<HTMLElement>()
const menuDropdown = ref<HTMLElement>()

// Methods
const toggleMenu = () => {
  if (props.disabled) return

  if (isOpen.value) {
    closeMenu()
  } else {
    openMenu()
  }
}

const openMenu = async () => {
  isOpen.value = true
  emit('menu-open')

  await nextTick()
  positionMenu()
}

const closeMenu = () => {
  isOpen.value = false
  emit('menu-close')
}

useExclusiveOverlay({
  isOpen,
  close: closeMenu
})

const positionMenu = () => {
  if (!menuWrapper.value || !menuDropdown.value) return

  const wrapperRect = menuWrapper.value.getBoundingClientRect()
  const dropdown = menuDropdown.value

  // Reset positioning
  dropdown.style.top = ''
  dropdown.style.bottom = ''
  dropdown.style.left = ''
  dropdown.style.right = ''
  dropdown.style.width = ''
  dropdown.style.minWidth = ''

  if (props.appendTo === 'body') {
    // Fixed positioning so dropdown overlays page (not clipped by sidebar)
    dropdown.style.position = 'fixed'
    dropdown.style.zIndex = '10000'
    if (props.position === 'top') {
      dropdown.style.bottom = `${window.innerHeight - wrapperRect.top + 4}px`
      dropdown.style.top = 'auto'
      dropdown.style.left = `${wrapperRect.left}px`
      dropdown.style.right = 'auto'
      dropdown.style.minWidth = '12rem'
    } else {
      dropdown.style.top = `${wrapperRect.bottom + 4}px`
      dropdown.style.bottom = 'auto'
      dropdown.style.left = `${wrapperRect.left}px`
      dropdown.style.right = 'auto'
      dropdown.style.minWidth = '12rem'
    }
    return
  }

  if (props.position === 'top') {
    dropdown.style.bottom = `${wrapperRect.height + 4}px`
    dropdown.style.left = '0'
    dropdown.style.right = '0'
  } else {
    dropdown.style.top = `${wrapperRect.height + 4}px`
    dropdown.style.left = '0'
    dropdown.style.right = '0'
  }
}

const handleItemClick = (item: MenuItem) => {
  if (item.disabled || item.type === 'separator') return

  emit('item-click', item)

  // Handle different action types
  if (item.handler) {
    item.handler()
  } else if (item.href) {
    // Navigate to href
    window.location.href = item.href
  }

  // Close menu after action (unless it's a submenu trigger)
  if (item.action !== 'submenu') {
    closeMenu()
  }
}

const handleTriggerKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    toggleMenu()
  } else if (event.key === 'Escape' && isOpen.value) {
    closeMenu()
  }
}

const handleMenuKeydown = (event: KeyboardEvent) => {
  switch (event.key) {
    case 'Escape':
      closeMenu()
      break
    case 'ArrowDown':
      event.preventDefault()
      focusNextItem()
      break
    case 'ArrowUp':
      event.preventDefault()
      focusPreviousItem()
      break
  }
}

const handleItemKeydown = (event: KeyboardEvent, item: MenuItem) => {
  if (item.disabled || item.type === 'separator') return

  switch (event.key) {
    case 'Enter':
    case ' ':
      event.preventDefault()
      handleItemClick(item)
      break
    case 'Escape':
      closeMenu()
      break
  }
}

const focusNextItem = () => {
  const focusableItems = menuDropdown.value?.querySelectorAll('[role="menuitem"]:not([tabindex="-1"])')
  if (!focusableItems?.length) return

  const currentIndex = Array.from(focusableItems).indexOf(document.activeElement as Element)
  const nextIndex = (currentIndex + 1) % focusableItems.length
    ; (focusableItems[nextIndex] as HTMLElement)?.focus()
}

const focusPreviousItem = () => {
  const focusableItems = menuDropdown.value?.querySelectorAll('[role="menuitem"]:not([tabindex="-1"])')
  if (!focusableItems?.length) return

  const currentIndex = Array.from(focusableItems).indexOf(document.activeElement as Element)
  const previousIndex = currentIndex <= 0 ? focusableItems.length - 1 : currentIndex - 1
    ; (focusableItems[previousIndex] as HTMLElement)?.focus()
}

// Click outside to close
const handleClickOutside = (event: Event) => {
  const target = event.target as Element
  if (!target.closest('.menu-wrapper')) {
    closeMenu()
  }
}

// Resize handler for repositioning
const handleResize = () => {
  if (isOpen.value) {
    positionMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
.menu-dropdown {
  position: absolute;
  z-index: 9999;
  min-width: 12rem;
  background-color: var(--color-surface-muted);
  border: 1px solid var(--color-border);
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  overflow: hidden;
}

.menu-dropdown--full-width {
  width: 100%;
}

.menu-item {
  display: block;
  width: 100%;
  cursor: pointer;
  
}

.menu-item:hover:not(.menu-item--disabled):not(.menu-item--separator) {
  background-color: var(--color-dropdown-option-hover);
}

.menu-item--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.menu-item--danger {
  color: var(--color-danger);
}

.menu-item--danger:hover:not(.menu-item--disabled) {
  background-color: var(--color-danger-muted);
  color: var(--color-danger);
}

.menu-item-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-text);
  text-decoration: none;
}

.menu-item-link {
  color: var(--color-text);
  text-decoration: none;
}

.menu-item-link:hover {
  color: var(--color-text);
  text-decoration: none;
}

.menu-item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.menu-item-label {
  flex: 1;
  font-weight: 500;
}

.menu-item-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.menu-item-shortcut {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  font-family: monospace;
  background-color: var(--color-surface-muted);
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
}

.menu-item-badge {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  background-color: var(--color-surface-muted);
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
}

.menu-separator {
  height: 1px;
  background-color: var(--color-border);
  margin: 0.25rem 0;
}

/* Transitions */
.menu-enter-active,
.menu-leave-active {
  
}

.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.menu-enter-to,
.menu-leave-from {
  opacity: 1;
  transform: scale(1);
}
</style>
